import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAppStore } from '../../store';
import { useWebSocket } from '../../hooks/useWebSocket';
import { notificationsApi } from '../../services/api';
import type { AppNotification } from '../../services/api';
import { Dashboard } from '../dashboard/Dashboard';
import { ExecutionMonitor } from '../execution/ExecutionMonitor';
import { ExecutionsModule } from '../execution/ExecutionsModule';
import { SystemsModule } from '../systems/SystemsModule';
import { AgentsModule } from '../agents/AgentsModule';
import { SettingsModule } from '../settings/SettingsModule';
import { InspectorModule } from '../inspector/InspectorModule';
import { DefectsModule } from '../defects/DefectsModule';
import { PagesModule } from '../pom/PagesModule';

// Roles con acceso a cada sección del nav
// owner/admin ven todo; tester no ve gestión avanzada; viewer igual que tester
const ALL_ROLES = ['owner', 'admin', 'tester', 'viewer'];
const ADMIN_ONLY = ['owner', 'admin'];
const TESTER_UP  = ['owner', 'admin', 'tester'];

const NAV_ITEMS = [
  { key: 'dashboard',  label: 'Dashboard',     icon: '◈', roles: ALL_ROLES },
  { key: 'systems',    label: 'Sistemas',       icon: '⬡', roles: ALL_ROLES },
  { key: 'executions', label: 'Ejecuciones',    icon: '▷', roles: ALL_ROLES },
  { key: 'agents',     label: 'Agentes',        icon: '◉', roles: TESTER_UP },
  { key: 'pages',      label: 'Pantallas',      icon: '◈', roles: TESTER_UP },
  { key: 'inspector',  label: 'Inspector',      icon: '🔍', roles: TESTER_UP },
  { key: 'defects',    label: 'Defectos',        icon: '🐛', roles: ALL_ROLES },
  { key: 'settings',   label: 'Configuración',  icon: '⚙', roles: ADMIN_ONLY },
] as const;
type Section = typeof NAV_ITEMS[number]['key'];

function NotificationItem({ notification, onClose }: {
  notification: { id: string; type: string; title: string; message?: string };
  onClose: () => void;
}) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const C: Record<string, { bg: string; border: string; text: string }> = {
    success: { bg: '#E1F5EE', border: '#9FE1CB', text: '#085041' },
    error:   { bg: '#FCEBEB', border: '#F7C1C1', text: '#501313' },
    warning: { bg: '#FAEEDA', border: '#FAC775', text: '#412402' },
    info:    { bg: '#E6F1FB', border: '#B5D4F4', text: '#042C53' },
  };
  const c = C[notification.type] ?? C.info;
  return (
    <div style={{ padding: '12px 16px', borderRadius: 12, background: c.bg, border: `1px solid ${c.border}`, color: c.text, pointerEvents: 'auto', maxWidth: 320, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', animation: 'slideInRight 0.25s ease-out' }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{notification.title}</div>
      {notification.message && <div style={{ fontSize: 11, opacity: 0.8, marginTop: 3 }}>{notification.message}</div>}
    </div>
  );
}

function NotificationToast() {
  const notifications = useAppStore(s => s.notifications);
  const removeNotification = useAppStore(s => s.removeNotification);
  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 9999, pointerEvents: 'none' }}>
      {notifications.map(n => <NotificationItem key={n.id} notification={n} onClose={() => removeNotification(n.id)} />)}
    </div>
  );
}

// ─── Notification Bell ───────────────────────────────────────────────────────

const TYPE_CFG: Record<string, { icon: string; color: string; bg: string }> = {
  exec_failed:    { icon: '✗', color: '#A32D2D', bg: '#FCEBEB' },
  exec_completed: { icon: '✓', color: '#0F6E56', bg: '#E1F5EE' },
  agent_offline:  { icon: '◉', color: '#5F5E5A', bg: '#F1EFE8' },
  new_user:       { icon: '●', color: '#185FA5', bg: '#E6F1FB' },
  ai_generation:  { icon: '✦', color: '#534AB7', bg: '#EEEDFE' },
};

function NotificationBell() {
  const [notifs, setNotifs] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    notificationsApi.list().then(setNotifs).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll cada 30s
    return () => clearInterval(t);
  }, [load]);

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unread = notifs.filter(n => !n.read).length;

  const handleMarkAll = async () => {
    await notificationsApi.markAllRead();
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleMarkOne = async (id: string) => {
    await notificationsApi.markRead([id]);
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ position: 'relative', border: 'none', background: open ? 'var(--color-background-secondary)' : 'transparent', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 16, color: 'var(--color-text-secondary)', transition: 'background 0.15s' }}
        title="Notificaciones"
      >
        🔔
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, borderRadius: 8, background: '#E24B4A', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 340, borderRadius: 12, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 500, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              Notificaciones {unread > 0 && <span style={{ fontSize: 11, color: '#E24B4A', fontWeight: 600 }}>({unread} nuevas)</span>}
            </span>
            {unread > 0 && (
              <button onClick={handleMarkAll} style={{ border: 'none', background: 'transparent', fontSize: 11, color: '#3B8BD4', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                Marcar todo leído
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifs.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>
                Sin notificaciones
              </div>
            ) : notifs.map(n => {
              const cfg = TYPE_CFG[n.type] ?? TYPE_CFG.exec_failed;
              return (
                <div
                  key={n.id}
                  onClick={() => !n.read && handleMarkOne(n.id)}
                  style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, alignItems: 'flex-start', background: n.read ? 'transparent' : 'var(--color-background-secondary)', cursor: n.read ? 'default' : 'pointer', transition: 'background 0.15s' }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: cfg.bg, color: cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                    {cfg.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: n.read ? 400 : 600, color: 'var(--color-text-primary)', marginBottom: 2 }}>{n.title}</div>
                    {n.message && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>}
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 3 }}>
                      {new Date(n.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  {!n.read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B8BD4', flexShrink: 0, marginTop: 4 }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function Sidebar({ collapsed, activeSection, onNavigate, onLogout, userRole }: {
  collapsed: boolean; activeSection: Section;
  onNavigate: (s: Section) => void; onLogout: () => void; userRole: string;
}) {
  const toggleSidebar = useAppStore(s => s.toggleSidebar);
  const email = localStorage.getItem('user_email') ?? '';
  const visibleItems = NAV_ITEMS.filter(item => (item.roles as readonly string[]).includes(userRole));
  return (
    <div style={{ width: collapsed ? 60 : 220, transition: 'width 0.25s ease', background: 'var(--color-background-secondary)', borderRight: '1px solid var(--color-border-tertiary)', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden', height: '100vh', position: 'sticky', top: 0 }}>
      <div style={{ padding: collapsed ? '20px 0' : '20px 16px', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', borderBottom: '1px solid var(--color-border-tertiary)', height: 64, boxSizing: 'border-box' }}>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Process Automation
          </div>
        )}
        {collapsed && (
          <img src='icon.png' />
        )}
        <button onClick={toggleSidebar} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 14, padding: 4 }}>
          {collapsed ? '→' : '←'}
        </button>
      </div>

      <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleItems.map(item => {
          const isActive = activeSection === item.key;
          return (
            <button key={item.key} onClick={() => onNavigate(item.key)} title={collapsed ? item.label : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10, justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '10px 0' : '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: isActive ? 'var(--color-background-primary)' : 'transparent', color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', fontSize: 13, fontWeight: isActive ? 600 : 400, transition: 'all 0.15s', width: '100%', boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.06)' : 'none' }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      <div style={{ padding: collapsed ? '12px 0' : '12px 16px', borderTop: '1px solid var(--color-border-tertiary)' }}>
        {!collapsed && email && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>}
        <button onClick={onLogout} title="Cerrar sesión"
          style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 8, justifyContent: collapsed ? 'center' : 'flex-start', width: '100%', padding: collapsed ? '8px 0' : '6px 8px', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 12, borderRadius: 6, transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#A32D2D'; e.currentTarget.style.background = '#FCEBEB'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}>
          <span style={{ fontSize: 14 }}>⎋</span>
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );
}

function PlaceholderSection({ title }: { title: string }) {
  return (
    <div style={{ height: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🚧</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-secondary)' }}>{title}</div>
      <div style={{ fontSize: 13, marginTop: 6 }}>Próximamente disponible.</div>
    </div>
  );
}

export function AppLayout({ onLogout }: { onLogout: () => void }) {
  const sidebarCollapsed = useAppStore(s => s.sidebarCollapsed);
  const activeSection = useAppStore(s => s.activeSection);
  const setSection = useAppStore(s => s.setSection);
  const currentUserRole = useAppStore(s => s.currentUserRole);

  // Conecta el WS de ejecuciones a nivel de app: sin esto, los eventos
  // run_started/step_complete/run_complete nunca llegan al store y el
  // Monitor se queda congelado en el estado del fetch inicial. Cada run
  // (fila de dataset) reporta su propio run_complete de forma independiente
  // — no espera a que terminen las demás filas del mismo caso.
  useWebSocket(localStorage.getItem('access_token'));

  const renderContent = () => {
    switch (activeSection as string) {
      case 'dashboard':  return <Dashboard />;
      case 'systems':    return <SystemsModule />;
      case 'executions': return <ExecutionsModule />;
      case 'agents':     return <AgentsModule />;
      case 'defects':    return <DefectsModule />;
      case 'settings':   return <SettingsModule />;
      case 'inspector':  return <InspectorModule />;
      case 'pages':      return <PagesModule />;
    }
  };

  // Use full-height layout for systems module
  const isFullHeight = activeSection === 'systems' || activeSection === 'executions' || activeSection === 'agents' || activeSection === 'settings' || (activeSection as string) === 'inspector' || (activeSection as string) === 'pages';

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        input, select, textarea, button { font-family: inherit; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--color-border-secondary); border-radius: 3px; }
        :root { --accent: #3B8BD4; --accent-soft: #E6F1FB; --step-bg: var(--color-background-secondary); --step-border: var(--color-border-tertiary); --step-body-bg: var(--color-background-primary); --chip-bg: var(--color-background-tertiary); }
        @keyframes slideInRight { from { opacity:0; transform:translateX(20px) } to { opacity:1; transform:translateX(0) } }
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
      `}</style>

      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--color-background-tertiary)', color: 'var(--color-text-primary)' }}>
        <Sidebar collapsed={sidebarCollapsed} activeSection={activeSection as never} onNavigate={s => setSection(s as typeof activeSection)} onLogout={onLogout} userRole={currentUserRole} />
        <main style={{ flex: 1, overflow: isFullHeight ? 'hidden' : 'auto', padding: isFullHeight ? '20px 24px' : '28px 32px', display: 'flex', flexDirection: 'column' }}>
          {/* Top bar con campana */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 4 }}>
            <NotificationBell />
          </div>
          {/* Page title for non-full-height sections */}
          {!isFullHeight && activeSection !== 'dashboard' && (
            <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              {NAV_ITEMS.find(n => (n.key as string) === activeSection)?.label}
            </h2>
          )}
          {isFullHeight && (
            <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              {NAV_ITEMS.find(n => (n.key as string) === activeSection)?.label}
            </h2>
          )}
          {renderContent()}
        </main>
      </div>
      <NotificationToast />
    </>
  );
}